import { useEffect, useState } from "react";
import { Palette, Save, Radar, Landmark, Mail } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth } from "../lib/auth";
import { fetchBusinessInfo, updateBusinessInfo } from "../lib/api";
import { invalidateBranding } from "../lib/company";

const FIELDS = [
  { key: "brand_name", label: "Sidebar brand (line 1)", ph: "ACME TOWING" },
  { key: "brand_tagline", label: "Sidebar tagline (line 2)", ph: "& Recovery · Dispatch" },
  { key: "app_title", label: "Browser tab title", ph: "Acme Towing Dispatch" },
  { key: "receipt_brand", label: "Receipt header brand", ph: "ACME TOWING & RECOVERY" },
  { key: "salvage_entity_name", label: "Salvage entity (short, e.g. ATR)", ph: "ATR" },
  { key: "salvage_entity_full", label: "Salvage entity (full name)", ph: "Acme Towing & Recovery" },
  { key: "tax_label", label: "Tax label", ph: "GST" },
  { key: "tax_rate_pct", label: "Tax rate (%)", ph: "15", type: "number" },
  { key: "depot_address", label: "Depot address (round-trip km)", ph: "123 Main Road, Yourtown, NZ", span: true },
];

/**
 * Company Branding & Locale (Settings → admin) — white-label configuration.
 * Everything here used to be hardcoded; new instances set their own values.
 */
export default function CompanyBrandingCard() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBusinessInfo()
      .then((c) => setCfg({ ...c, tax_rate_pct: c.tax_rate != null ? +(c.tax_rate * 100).toFixed(2) : 15 }))
      .catch(() => toast.error("Failed to load company branding"));
  }, []);

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    const pct = parseFloat(cfg.tax_rate_pct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Tax rate must be a percentage between 0 and 100");
      return;
    }
    setSaving(true);
    try {
      const next = await updateBusinessInfo({
        brand_name: cfg.brand_name,
        brand_tagline: cfg.brand_tagline,
        app_title: cfg.app_title,
        receipt_brand: cfg.receipt_brand,
        salvage_entity_name: cfg.salvage_entity_name,
        salvage_entity_full: cfg.salvage_entity_full,
        tax_label: cfg.tax_label,
        tax_rate: +(pct / 100).toFixed(4),
        depot_address: cfg.depot_address,
        gps_provider: cfg.gps_provider || "telematics_guru",
        bank_name: cfg.bank_name || "",
        bank_account_name: cfg.bank_account_name || "",
        bank_account_number: cfg.bank_account_number || "",
        email_from_name: cfg.email_from_name || "",
        email_from_address: cfg.email_from_address || "",
        email_reply_to: cfg.email_reply_to || "",
      });
      setCfg({ ...next, tax_rate_pct: next.tax_rate != null ? +(next.tax_rate * 100).toFixed(2) : 15 });
      invalidateBranding();
      if (next.app_title) document.title = next.app_title;
      toast.success("Company branding saved");
      // Saved but unmappable: the tow calculator quotes round-trip km from
      // this pin and can't quote at all without one, so say it out loud
      // instead of letting a green tick imply the depot is set.
      if ((next.depot_address || "").trim() && next.depot_lat == null) {
        toast.warning(
          "Depot saved, but we couldn't find it on the map — the Tow Calculator can't quote until it's pinned. Try adding the suburb, city and country.",
          { duration: 9000 },
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;
  if (!cfg) {
    return (
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6">
        <p className="text-sm text-slate-500">Loading company branding…</p>
      </section>
    );
  }

  return (
    <section
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6 space-y-4"
      data-testid="company-branding-card"
    >
      <div className="flex items-center gap-2">
        <Palette className="h-5 w-5 text-slate-700 dark:text-slate-300" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">
          Company Branding &amp; Locale
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
        White-label settings — controls the app name in the sidebar and browser tab, receipt branding,
        the salvage-entity name shown on write-off paperwork, the tax rate applied to storage quotes
        and invoices, and the depot used for round-trip km calculations.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.span ? "md:col-span-2" : undefined}>
            <Label className="uppercase-label">{f.label}</Label>
            <Input
              type={f.type || "text"}
              value={cfg[f.key] ?? ""}
              placeholder={f.ph}
              onChange={(e) => upd(f.key, e.target.value)}
              data-testid={`cb-${f.key.replace(/_/g, "-")}`}
            />
            {/* The depot is the origin of every round-trip km. Show the pin
                we actually resolved, so an address that saved but didn't map
                can't sit there looking fine. */}
            {f.key === "depot_address" && (
              <p
                className={`text-[11px] mt-1 ${cfg.depot_lat != null ? "text-slate-500 dark:text-slate-400" : "text-amber-600 dark:text-amber-400"}`}
                data-testid="cb-depot-pin"
              >
                {cfg.depot_lat != null
                  ? `Pinned at ${Number(cfg.depot_lat).toFixed(5)}, ${Number(cfg.depot_lng).toFixed(5)} — round-trip km measure from here.`
                  : "Not pinned yet — save this address to place it on the map. Until it's pinned, the tow calculator can't quote."}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Feb 2026 — Fleet tracking mode. Each tenant picks how they feed
          truck positions into Tow-Hook. Independent from branding but
          shares this card so setup is one screen. */}
      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-1">
          <Radar className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          <Label className="uppercase-label !m-0">Fleet Tracking Mode</Label>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          How does your fleet share position data with Tow-Hook? All modes write
          to the same map + auto-arrival pipeline — you only need one.
        </p>
        <Select
          value={cfg.gps_provider || "telematics_guru"}
          onValueChange={(v) => upd("gps_provider", v)}
        >
          <SelectTrigger className="max-w-md" data-testid="cb-gps-provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="telematics_guru" data-testid="cb-gps-tg">
              Telematics Guru (hardware tracker in each truck)
            </SelectItem>
            <SelectItem value="phone" data-testid="cb-gps-phone">
              Driver phone GPS (free — uses the tablet in the truck)
            </SelectItem>
            <SelectItem value="manual" data-testid="cb-gps-manual">
              None / manual (static markers, no auto-arrival or ETA)
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 leading-snug">
          {cfg.gps_provider === "phone" && (
            <>Drivers will be asked to allow location access on their tablet. Positions update every ~30 seconds while the driver page is open. Battery drain is minimal — around 4% over an 8-hour shift in our testing.</>
          )}
          {cfg.gps_provider === "manual" && (
            <>The map view will still show trucks but at their last-known / assigned depot position. Drivers tap &ldquo;At Scene&rdquo; manually — no geofence auto-arrival. Dispatch ETA is disabled.</>
          )}
          {(cfg.gps_provider === "telematics_guru" || !cfg.gps_provider) && (
            <>Configure Telematics Guru credentials in the &ldquo;Telematics Guru&rdquo; integration card below. Hardware-in-cab tracking gives the most accurate geofence and ETA.</>
          )}
        </p>
      </div>

      {/* Feb 2026 — Bank details for the Email Proforma flow. Rendered
          on the proforma PDF's "How to pay" callout so customers can pay
          by online banking. Optional — only needed if this tenant emails
          proformas rather than taking payment on release. */}
      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-1">
          <Landmark className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          <Label className="uppercase-label !m-0">Payment / Bank Details</Label>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Printed on proforma invoices sent to customers so they can pay by
          online banking. Leave blank if you take payment on release only.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="uppercase-label">Bank</Label>
            <Input
              type="text"
              value={cfg.bank_name ?? ""}
              placeholder="ANZ / Kiwibank / Westpac / …"
              onChange={(e) => upd("bank_name", e.target.value)}
              data-testid="cb-bank-name"
            />
          </div>
          <div>
            <Label className="uppercase-label">Account name</Label>
            <Input
              type="text"
              value={cfg.bank_account_name ?? ""}
              placeholder="Acme Towing Ltd"
              onChange={(e) => upd("bank_account_name", e.target.value)}
              data-testid="cb-bank-account-name"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="uppercase-label">Account number</Label>
            <Input
              type="text"
              value={cfg.bank_account_number ?? ""}
              placeholder="01-1234-5678901-00"
              onChange={(e) => upd("bank_account_number", e.target.value)}
              data-testid="cb-bank-account-number"
            />
          </div>
        </div>
      </div>

      {/* Oct 2026 — per-tenant system email sender (password resets,
          support requests). One shared Resend account; each tenant
          verifies their own sending domain in it. */}
      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          <Label className="uppercase-label !m-0">System Email Sender</Label>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Used for password-reset and support emails sent to your own staff. Leave the
          address blank and we send from the Tow-Hook verified sender with your brand
          name on it. To send from your own domain, verify it with us first — otherwise
          your resets may land in spam.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="uppercase-label">From name</Label>
            <Input
              type="text"
              value={cfg.email_from_name ?? ""}
              placeholder={cfg.brand_name || "Acme Towing"}
              onChange={(e) => upd("email_from_name", e.target.value)}
              data-testid="cb-email-from-name"
            />
          </div>
          <div>
            <Label className="uppercase-label">From address (verified domain)</Label>
            <Input
              type="email"
              value={cfg.email_from_address ?? ""}
              placeholder="no-reply@acmetowing.co.nz"
              onChange={(e) => upd("email_from_address", e.target.value)}
              data-testid="cb-email-from-address"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="uppercase-label">Reply-to address</Label>
            <Input
              type="email"
              value={cfg.email_reply_to ?? ""}
              placeholder="office@acmetowing.co.nz"
              onChange={(e) => upd("email_reply_to", e.target.value)}
              data-testid="cb-email-reply-to"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="cb-save">
          <Save className="h-4 w-4.5" />
          {saving ? "Saving…" : "Save Branding"}
        </Button>
      </div>
    </section>
  );
}
